import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import {
  numberFormatter,
  numberFormatterOnlyInM,
} from "../../../utils/formatter";
import { EmptyMessageTemplate } from "../../compliance-monitoring/cost-compliance";

const tabs = ["Vendors",
  "L3",
  "District",
  "Plant"];
const tabVariants: any = {
  L3: "l3_category",
  District: "district",
  Plant: "plant",
  Vendors: "vendor",
};
const lbsTemplate = (node: any, maxLbs: number) => {
  const percentage = (node?.total_volume / maxLbs) * 100;

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1 h-6 bg-gray-100 rounded">
        <div
          className="absolute h-full rounded"
          style={{
            width: `${percentage}%`,
            background: "#86BC25",
          }}
        />
      </div>
      <span className="min-w-[fit-content] text-right">
        {numberFormatterOnlyInM(node?.total_volume, 1)}
      </span>
    </div>
  );
};

const spendTemplate = (node: any, maxSpend: number) => {
  const percentage = (node?.total_spend / maxSpend) * 100;

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1 h-6 bg-gray-100 rounded-md">
        <div
          className="absolute h-full rounded"
          style={{
            width: `${percentage}%`,
            background: "#86BC25",
          }}
        />
      </div>
      <span className="text-right">
        {numberFormatterOnlyInM(node?.total_spend, 1, "$")}
      </span>
    </div>
  );
};

const PurchaseOverviewVendor = ({
  purchaseOverviewData,
  purchaseOrSales,
}: any) => {
  const [navItem, setNavItem] = useState<string>("Vendors");
  const [tableData, setTableData] = useState([]);
  const [maxVolume, setMaxVolume] = useState<number>(0);
  const [maxSpend, setMaxSpend] = useState<number>(0);

  useEffect(() => {
    if (purchaseOrSales === "Sales") {
      setNavItem("L3");
    } else {
      setNavItem("Vendors");
    }
  }, [purchaseOrSales]);

  useEffect(() => {
    if (purchaseOverviewData) {
      if (navItem === "L3") {
        setTableData(purchaseOverviewData?.l3Categories);
        setMaxVolume(purchaseOverviewData?.l3MaxVolume);
        setMaxSpend(purchaseOverviewData?.l3MaxSpend);
      } else if (navItem === "Vendors") {
        setTableData(purchaseOverviewData?.vendors);
        setMaxVolume(purchaseOverviewData?.vendorMaxVolume);
        setMaxSpend(purchaseOverviewData?.vendorMaxSpend);
      } else if (navItem === "Plant") {
        setTableData(purchaseOverviewData?.plants);
        setMaxVolume(purchaseOverviewData?.plantMaxVolume);
        setMaxSpend(purchaseOverviewData?.plantMaxSpend);
      } else {
        setTableData(purchaseOverviewData?.districts);
        setMaxVolume(purchaseOverviewData?.districtMaxVolume);
        setMaxSpend(purchaseOverviewData?.districtMaxSpend);
      }
    }
  }, [purchaseOverviewData, navItem]);


  // const generateCsvData = (arrayOfObjects: any[]) => {
  //   if (!arrayOfObjects || arrayOfObjects.length === 0) {
  //     return [];
  //   }

  //   // decide total column name based on purchaseOrSales
  //   const totalKey = purchaseOrSales === "Sales" ? "total_sales" : "total_spends";

  //   // clone and add formatted total / volume
  //   const newArrayOfObjects = arrayOfObjects.map((item: any) => {
  //     const copy = { ...item };
  //     copy[totalKey] = copy["total_spend"]?.toLocaleString("en-US", {
  //       style: "currency",
  //       currency: "USD",
  //     });
  //     copy["total_volume"] = Math.trunc(copy["total_volume"]);
  //     return copy;
  //   });

  //   // build headers from original object keys, replacing total_spend with chosen totalKey
  //   const originalKeys = Object.keys(arrayOfObjects[0]);
  //   const headers = originalKeys.map((k) =>
  //     k === "total_spend"
  //       ? totalKey
  //       : purchaseOrSales === "Sales" && navItem === "Plant" && k === "plant"
  //         ? "sales_office"
  //         : k
  //   );

  //   // map rows using headers; handle sales_office mapping
  //   const rows = newArrayOfObjects.map((obj: any) =>
  //     headers.map((header) =>
  //       header === "sales_office" ? obj["plant"] : obj[header]
  //     )
  //   );

  //   return [headers, ...rows];
  // };

  const generateCsvData = (arrayOfObjects: any[]) => {
    if (!arrayOfObjects || arrayOfObjects.length === 0) {
      return [];
    }

    // decide total column name based on purchaseOrSales
    const totalKey = purchaseOrSales === "Sales" ? "total_sales" : "total_spend";

    // clone and add formatted total / volume
    const newArrayOfObjects = arrayOfObjects.map((item: any) => {
      const copy = { ...item };
      copy[totalKey] = copy["total_spend"]?.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
      copy["total_volume"] = Math.trunc(copy["total_volume"]);
      return copy;
    });

    // build headers from original object keys, replacing total_spend with chosen totalKey
    const originalKeys = Object.keys(arrayOfObjects[0]);
    const headers = originalKeys.map((k) =>
      k === "total_spend"
        ? totalKey
        : k === "vendor"
          ? "supplier"
          : purchaseOrSales === "Sales" && navItem === "Plant" && k === "plant"
            ? "sales_office"
            : k
    );


    // map rows using headers; handle sales_office mapping
    const rows = newArrayOfObjects.map((obj: any) =>
      headers.map((header) =>
        header === "sales_office" ? obj["plant"] : header === "supplier" ? obj["vendor"] : obj[header]
      )
    );

    return [headers, ...rows];
  };

  // const generateCsvData = (arrayOfObjects: any[]) => {
  //   if (!arrayOfObjects || arrayOfObjects.length === 0) {
  //     return []; // Return an empty array if input is empty
  //   }

  //   // decide total column name based on purchaseOrSales
  //   const totalKey = purchaseOrSales === "Sales" ? "total_sales" : "total_spends";


  //   // Extract headers (keys of the first object)
  //   let headers = Object.keys(arrayOfObjects[0]).filter(
  //     (item: string) => item !== "total_spend"
  //   );
  //   headers = headers.map((ele) =>
  //     purchaseOrSales === "Sales" && navItem === "Plant" && ele === "plant"
  //       ? "sales_office"
  //       : ele
  //   );

  //   let newArrayOfObjects = [...arrayOfObjects];

  //   newArrayOfObjects = newArrayOfObjects?.map((item: any) => {
  //     item["total_spends"] = item["total_spend"].toLocaleString("en-US", {
  //       style: "currency",
  //       currency: "USD",
  //     });
  //     item["total_volume"] = Math.trunc(item["total_volume"]);
  //     return item;
  //   });

  //   // Map each object to an array of its values
  //   const rows = newArrayOfObjects.map((obj) =>
  //     headers.map((header) =>
  //       purchaseOrSales === "Sales" &&
  //         navItem === "Plant" &&
  //         header === "sales_office"
  //         ? obj["plant"]
  //         : obj[header]
  //     )
  //   );
  //   // Combine headers and rows into a single 2D array
  //   const csvData = [headers, ...rows];

  //   return csvData;
  // };



  const csvData = generateCsvData([...tableData]);
  return (
    <div className="p-[20px] h-full w-full">
      <div className="flex justify-between">
        <div className="text-[1.5vw]">
          {purchaseOrSales} Overview Across{" "}
          {purchaseOrSales === "Sales" && navItem === "Plant"
            ? "Sales Office"
            : navItem === "Vendors" ? "Suppliers" : navItem}
        </div>
        <div className="flex items-center justify-center">
          <div className="text-white bold text-[0.8vw]  bg-[#86BC25] p-[0.4vw] rounded-md  cursor-pointer flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1vw"
              height="1vw"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                fill="white"
              />
            </svg>
            <span className="ml-[3px]">
              <CSVLink
                filename={
                  `${purchaseOrSales === "Sales" && navItem === "Plant"
                    ? "Sales_Office"
                    : navItem === "Vendors" ? "Suppliers" : navItem
                  }` + "_Data"
                }
                className="no-hover"
                style={{ textDecoration: "none" }}
                data={csvData}
              >
                Download
              </CSVLink>
            </span>
          </div>
        </div>
      </div>
      <div className="mt-5">
        <div className="flex justify-start border-b-2 border-[#eaedf4] items-center gap-6 text-black font-normal text-[2vh]  max-h-500:mt-[37px]">
          {tabs
            ?.filter((item) => {
              if (purchaseOrSales === "Sales" && item === "Vendors") {
                return false;
              } else return true;
            })
            .map((item) => {
              return (
                <span
                  key={item}
                  className={
                    (navItem === item
                      ? "font-bold text-[#86BC25] border-b-2 border-[#86BC25]"
                      : "") + " cursor-pointer pb-[1vh]"
                  }
                  onClick={() => setNavItem(item)}
                >
                  {purchaseOrSales === "Sales" && item === "Plant"
                    ? "Sales Office"
                    : item === "Vendors" ? "Suppliers" : item}
                </span>
              );
            })}
        </div>
      </div>
      <div className="cost-compliance-table mt-5">
        {" "}
        <DataTable
          emptyMessage={EmptyMessageTemplate}
          scrollable
          scrollHeight="47.5vh"
          showGridlines
          value={tableData ?? []}
          stripedRows
        >
          <Column
            className={
              navItem === "Vendors" ||
                navItem === "L3" ||
                (purchaseOrSales === "Sales" && navItem === "Plant")
                ? "ps-vendor-l3-width"
                : "ps-plant-district-width"
            }
            field="name"
            header={
              purchaseOrSales === "Sales" && navItem === "Plant"
                ? "Sales Office"
                : navItem === "Vendors" ? "Suppliers" : navItem
            }
            body={(node) => {
              return <span>{node[tabVariants[navItem]]}</span>;
            }}
          ></Column>
          <Column
            field="total_spend"
            header={purchaseOrSales === "Sales" ? "Sales" : "Spend"}
            body={(node) => {
              return spendTemplate(node, maxSpend);
            }}
          ></Column>
          <Column
            field="total_volume"
            header="lbs."
            body={(node) => {
              return lbsTemplate(node, maxVolume);
            }}
          ></Column>
        </DataTable>
      </div>
    </div>
  );
};

export default PurchaseOverviewVendor;
